import { Chip, CircularProgress, Tooltip } from '@mui/material';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useSyncStatus, type SyncState } from '../sync/status';

const LABEL: Record<SyncState, string> = {
  disabled: 'Sync off',
  offline: 'Offline',
  syncing: 'Syncing…',
  synced: 'Synced',
  error: 'Sync failed',
  conflicted: 'Conflicts',
};

/** Live cloud-sync state for the top bar. Hidden entirely when sync is off. */
export default function SyncStatusChip({ onClick }: { onClick?: () => void }) {
  const status = useSyncStatus();
  if (status.state === 'disabled') return null;

  const label = LABEL[status.state];
  const detail =
    status.state === 'conflicted'
      ? `${status.conflicts} conflict${status.conflicts === 1 ? '' : 's'} need your attention`
      : status.state === 'error'
        ? status.error ?? 'Sync failed'
        : status.state === 'offline'
          ? 'Changes are saved locally and will sync when you reconnect'
          : status.lastSyncedAt
            ? `Last synced ${new Date(status.lastSyncedAt).toLocaleTimeString()}${status.pending ? ` · ${status.pending} pending` : ''}`
            : 'Cloud sync is on';

  const common = { size: 'small' as const, variant: 'outlined' as const, onClick };

  if (status.state === 'syncing')
    return <Chip {...common} icon={<CircularProgress size={12} />} label={label} />;
  if (status.state === 'synced')
    return (
      <Tooltip title={detail}>
        <Chip
          {...common}
          color={status.pending > 0 ? 'default' : 'success'}
          icon={<CloudDoneIcon />}
          label={status.pending > 0 ? `${status.pending} pending` : label}
        />
      </Tooltip>
    );
  if (status.state === 'offline')
    return (
      <Tooltip title={detail}>
        <Chip
          {...common}
          color="default"
          icon={<CloudOffIcon />}
          label={status.pending > 0 ? `Offline · ${status.pending} pending` : label}
        />
      </Tooltip>
    );
  if (status.state === 'conflicted')
    return (
      <Tooltip title={detail}>
        <Chip {...common} color="warning" icon={<WarningAmberIcon />} label={`${label} (${status.conflicts})`} />
      </Tooltip>
    );
  return (
    <Tooltip title={detail}>
      <Chip {...common} color="error" icon={<SyncProblemIcon />} label={label} />
    </Tooltip>
  );
}
